import { useMemo } from "react";
import { useSubscription } from "@/hooks/use-subscription";
import { useCredits } from "@/hooks/use-credits";
import { useUsageStats } from "@/hooks/use-usage-stats";
import { usePlans } from "@/hooks/use-plans";
import type { Plan } from "@/types";

export interface PlanData {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: string;
  active: boolean;
  type: "paid" | "free";
  priceId: string;
  productId: string;
  // Additional plan details from Stripe plans
  features?: string[];
  metadata?: Record<string, any>;
  credits?: number;
}

export interface CreditsData {
  remaining: number;
  unlimited: boolean;
  payAsYouGoEnabled: boolean;
}

export interface UsageData {
  currentUsage: number;
  usagePercentage: number;
}

export interface PlanAndCreditsData {
  plans: Plan[];
  currentPlan: PlanData | null;
  creditsData: CreditsData;
  usageData: UsageData;
  loading: boolean;
  error: string | null;
  plansLoading: boolean;
  plansError: string | null;
  refetchCredits: () => Promise<void>;
  refetchPlans: () => Promise<void>;
}

export const usePlanAndCredits = (): PlanAndCreditsData => {
  const {
    subscription,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription();

  const {
    credits,
    loading: creditsLoading,
    error: creditsError,
    refetch: refetchCredits,
  } = useCredits();

  const {
    usageStats,
    loading: usageLoading,
    error: usageError,
  } = useUsageStats();

  const {
    plans,
    loading: plansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = usePlans();

  // Memoize the current plan to prevent unnecessary recalculations
  const currentPlan: PlanData | null = useMemo(() => {
    if (
      !subscription ||
      subscription.status !== "active" ||
      !subscription.items?.data?.[0]
    ) {
      return null;
    }

    const subscriptionItem = subscription.items.data[0];
    const priceId = subscriptionItem.price.id || subscription.plan?.id;

    // Find the matching plan from the plans list to get additional details
    const matchingPlan = plans.find(
      (plan) => plan.priceId === priceId || plan.id === priceId
    );

    return {
      id: subscriptionItem.price.id,
      name:
        matchingPlan?.name ||
        subscriptionItem.price.nickname ||
        subscription.plan?.nickname ||
        "Subscription Plan",
      description: matchingPlan?.description || "Active subscription plan",
      price:
        subscriptionItem.price.unit_amount ||
        subscription.plan?.amount ||
        matchingPlan?.price ||
        0,
      currency:
        subscriptionItem.price.currency ||
        subscription.plan?.currency ||
        subscription.currency ||
        matchingPlan?.currency ||
        "usd",
      interval:
        subscriptionItem.price.recurring?.interval ||
        subscription.plan?.interval ||
        "month",
      active: true, // Since we already checked subscription.status === 'active'
      type: "paid",
      priceId: subscriptionItem.price.id || subscription.plan?.id || "",
      productId: subscriptionItem.price.product || subscription.plan?.product || "",
      // Additional details from the plans data
      features: matchingPlan?.features || [],
      metadata: matchingPlan?.metadata || {},
      credits: matchingPlan?.credits,
    };
  }, [subscription, plans]);

  // Memoize credits data
  const creditsData: CreditsData = useMemo(
    () => ({
      remaining: credits?.remaining || 0,
      unlimited: credits?.unlimited || false,
      payAsYouGoEnabled: credits?.payAsYouGoEnabled || false,
    }),
    [credits]
  );

  // Memoize usage data
  const usageData: UsageData = useMemo(() => {
    const currentUsage = usageStats?.requestsThisMonth || 0;

    const usagePercentage = creditsData.unlimited
      ? 0 // Show 0% for unlimited credits
      : creditsData.remaining > 0
        ? Math.max(
            0,
            (currentUsage / (creditsData.remaining + currentUsage)) * 100
          )
        : 100; // 100% if no credits remaining

    return {
      currentUsage,
      usagePercentage,
    };
  }, [usageStats, creditsData]);

  // Memoize loading and error states
  const loading = useMemo(
    () => subscriptionLoading || creditsLoading || usageLoading || plansLoading,
    [subscriptionLoading, creditsLoading, usageLoading, plansLoading]
  );

  const error = useMemo(
    () => subscriptionError || creditsError || usageError || plansError,
    [subscriptionError, creditsError, usageError, plansError]
  );

  return useMemo(
    () => ({
      currentPlan,
      plans,
      creditsData,
      usageData,
      loading,
      error,
      plansLoading,
      plansError,
      refetchCredits,
      refetchPlans,
    }),
    [currentPlan, creditsData, usageData, plans, loading, error]
  );
};
